import { useCallback, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import uploadFileHandler, { UploadableFile } from './upload-file-handler'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { FileUploadStatusPopover } from './FileUploadStatusPopover'
import { kAcceptedFilesList } from '@/constants/constants-gcs'
import { StorageReference } from 'firebase/storage'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { validatePdfPageCount, isPdfFile } from './utils/files-utils'
import { getRelevantSegments } from '@/content-viewer/components/common/save-to-drive-modal/utils/folder-name-utils'
import DropZone from '@/components/drop-zone/DropZone'
import { useAppSelector } from '@/store/store-hooks'
import { selectActiveMatter, selectMatterById } from '@/matter/store/selectors'
import { RootState } from '@/store/store'
import { Link } from 'react-router-dom'
import { MatterFolder, SlashCircle } from '@/assets/icons'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { kMattersRoute } from '@/matter/constants'

export type UploadTaskStatus = {
  progress: number
  complete: boolean
  errorMessage: string
  uploadableFile: UploadableFile
}

export type FilesDropZoneProps = {
  onTaskListItemUpdated: (task: UploadTaskStatus, allTasks: Map<string, UploadTaskStatus>) => void
  currentFolder: StorageReference | null
  hideUploadProgressOverlay?: boolean
  singleFileLimit?: boolean
  showFolderName?: boolean
}

export interface FilesDropZoneRef {
  openSystemFileSelector: () => void
}

const FilesDropZone = function FilesDropZone(props: FilesDropZoneProps) {
  const { onTaskListItemUpdated, currentFolder, singleFileLimit, hideUploadProgressOverlay = false } = props
  const { trackEvent } = useAnalytics()

  const [uploadTasks, setUploadTasks] = useState(new Map<string, UploadTaskStatus>())
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [dropError, setDropError] = useState<string | null>(null)

  // Upload guards - only allow uploads if inside a matter folder and not in the unassigned matter context
  // Use active matter from Redux (set from URL) to detect unassigned matter context,
  // since unassigned matter files live at drive root (no folder structure to detect from).
  const activeMatter = useAppSelector(selectActiveMatter)
  const isInUnassignedMatterContext = isUnassignedMatter(activeMatter)

  // For standard matters, check if inside a matter folder based on storage path
  const relevantSegments = currentFolder ? getRelevantSegments(currentFolder.fullPath) : []
  const folderMatterId = relevantSegments[0] // First segment after users/uid is the matterId
  const folderMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: folderMatterId }))
  const isInsideMatterFolder = folderMatter !== null

  // Upload is disabled when:
  // - In unassigned matter context (view only, files at drive root)
  // - Not inside any matter folder (must navigate into a matter folder first)
  const uploadDisabled = isInUnassignedMatterContext || !isInsideMatterFolder

  // On Drop
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setDropError(null)

      if (acceptedFiles.length === 0) {
        console.log('No accepted files to upload.')
        return
      }

      // Open the upload progress drawer
      setDrawerOpen(true)

      acceptedFiles.forEach(async (file) => {
        // Create a unique persistent id for the file
        const fileId = nanoid(22)

        // Create an Uploadable File
        const uploadableFile: UploadableFile = {
          file: file,
          fileName: file.name,
          id: fileId,
        }

        // Validate PDF page count if applicable
        if (isPdfFile(file)) {
          const validationError = await validatePdfPageCount(file)
          if (validationError?.error) {
            // Add failed task with error message
            const failedTask: UploadTaskStatus = {
              progress: 0,
              complete: false,
              errorMessage: validationError.message,
              uploadableFile: uploadableFile,
            }
            setUploadTasks((prevTasks) => new Map(prevTasks).set(failedTask.uploadableFile.id, failedTask))
            return
          }
        }

        // Initialize the task and add it to state
        const newUploadTask: UploadTaskStatus = {
          progress: 0,
          complete: false,
          errorMessage: '',
          uploadableFile: uploadableFile,
        }
        setUploadTasks((prevTasks) => new Map(prevTasks).set(newUploadTask.uploadableFile.id, newUploadTask))

        // Create a new upload task for this file and pass event handlers to update state based on upload progress and status
        uploadFileHandler({
          uploadableFile,
          currentFolder,
          onProgress: function (progress: number): void {
            setUploadTasks((prevTasks) => {
              const thisFileStatus = prevTasks.get(uploadableFile.id)
              if (thisFileStatus === undefined) {
                Sentry.captureException(new Error(`thisFileStatus is undefined, cannot update file upload task onProgress().`))
                return prevTasks // return current state if no updates are made
              }

              const updatedTask: UploadTaskStatus = {
                ...thisFileStatus,
                progress: progress,
                complete: false,
                errorMessage: '',
              }

              // Update the list of all tasks
              const updatedTasks = new Map(prevTasks).set(uploadableFile.id, updatedTask)

              // Call the onTaskListItemUpdated callback with the updated task
              Promise.resolve().then(() => onTaskListItemUpdated(updatedTask, updatedTasks))

              return updatedTasks
            })
          },
          onError: function (error: Error): void {
            setUploadTasks((prevTasks) => {
              const thisFileStatus = prevTasks.get(uploadableFile.id)
              if (thisFileStatus === undefined) {
                Sentry.captureException(new Error(`thisFileStatus is undefined, cannot update file upload task onError(). Error message: ${error.message}`))
                return prevTasks
              }

              const updatedTask: UploadTaskStatus = {
                ...thisFileStatus,
                errorMessage: 'Error uploading file',
              }

              // Update the list of all tasks
              const updatedTasks = new Map(prevTasks).set(uploadableFile.id, updatedTask)

              // Call the onTaskListItemUpdated callback with the updated task
              Promise.resolve().then(() => onTaskListItemUpdated(updatedTask, updatedTasks))

              return updatedTasks
            })
          },
          onComplete: function (): void {
            // Set it as completed
            setUploadTasks((prevTasks) => {
              const thisFileStatus = prevTasks.get(uploadableFile.id)
              if (thisFileStatus === undefined) {
                Sentry.captureException(new Error('thisFileStatus is undefined, cannot update file upload task onComplete().'))
                return prevTasks
              }

              const updatedTask: UploadTaskStatus = {
                ...thisFileStatus,
                complete: true,
              }

              // Update the list of all tasks
              const updatedTasks = new Map(prevTasks).set(uploadableFile.id, updatedTask)

              // Call the onTaskListItemUpdated callback with the updated task
              Promise.resolve().then(() => onTaskListItemUpdated(updatedTask, updatedTasks))

              trackEvent(AnalyticsEvent.FileAnalysisFileUpload)

              // Return the updated state
              return updatedTasks
            })
          },
        })
      })
    },
    [currentFolder, onTaskListItemUpdated, trackEvent] // depend on this and use latest version when running
  )

  // On Drop Rejected
  const onDropRejected = (fileRejections: FileRejection[]) => {
    console.log('FileRejections: ', fileRejections)
    if (singleFileLimit && fileRejections.length > 1) {
      setDropError('Please upload a single file. Use your drive to upload multiple files.')
      return
    }

    fileRejections.forEach((fileRejection: FileRejection) => {
      const extensionMatch = fileRejection?.file?.path?.match(/\.[0-9a-z]+$/i)
      const extension = extensionMatch ? extensionMatch[0] : 'unknown'
      const errorMessage = `Unsupported file type ${extension} or size exceeds 1GB limit.`

      // Create a unique persistent id for the file
      const fileId = nanoid(22)

      // Create an Uploadable File
      const uploadableFile: UploadableFile = {
        file: fileRejection.file,
        fileName: fileRejection.file.name,
        id: fileId,
      }

      // Initialize the task and add it to state
      const newUploadTask: UploadTaskStatus = {
        progress: 0,
        complete: false,
        errorMessage: errorMessage,
        uploadableFile: uploadableFile,
      }

      setUploadTasks((prevTasks) => new Map(prevTasks).set(newUploadTask.uploadableFile.id, newUploadTask))
    })
  }

  // Disabled state content, based on whether the user is in the unassigned matter folder or not inside a matter folder
  const disabledTitle = isInUnassignedMatterContext
    ? 'Uploads aren’t available in this space'
    : !isInsideMatterFolder
    ? 'Upload Files Inside a Matter'
    : undefined
  const disabledSubtitle = uploadDisabled ? (
    <span>
      {isInUnassignedMatterContext ? (
        <>"Unassigned" is view only. Upload or move files into a matter to continue. </>
      ) : (
        <>
          Select a Matter below to upload files and keep work case-specific.
          <br />
          Don't see the matter you need? {''}
        </>
      )}
      <Link to={kMattersRoute} className="text-brand-500 underline underline-offset-2">
        Manage Matters
      </Link>
    </span>
  ) : undefined

  return (
    <>
      <DropZone
        onFilesAccepted={onDrop}
        onFilesRejected={onDropRejected}
        acceptedFileTypes={kAcceptedFilesList}
        errorMessage={dropError ?? undefined}
        disabled={uploadDisabled}
        multiple={!singleFileLimit}
        title={disabledTitle}
        subtitle={disabledSubtitle}
        icon={
          isInUnassignedMatterContext ? (
            <SlashCircle className="w-5 h-5 text-brand-neutral-400" />
          ) : !isInsideMatterFolder ? (
            <MatterFolder className="w-5 h-5" />
          ) : undefined
        }
      />
      {!hideUploadProgressOverlay && (
        <FileUploadStatusPopover tasks={uploadTasks} expanded={drawerOpen} setExpanded={(newValue: boolean) => setDrawerOpen(newValue)} />
      )}
    </>
  )
}

export default FilesDropZone
